import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { batch } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { populateAnalysisThunk } from '../../../redux/slices/analyses';
import { loadAnalysisAction } from '../../../redux/slices/loadingAnalyses';
import { GET_ANALYSIS } from '../../../utils/apollo/analysis';
import {
  IAnalysisQuery,
  IAnalysisQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import { generateScratchpadAnalysisId } from '../../../redux/utils/transientAnalysis';
import { useCurrentUrl } from '../../../utils/routing';
import { isAnalysisLoadedSelector } from '../../../redux/selectors/analyses';

interface UseScratchpadAnalysisProps {
  analysisId: string;
  refresh?: boolean;
  shouldLoad?: boolean;
}

/**
 * This hook is used to load an analysis into the scratchpad.
 * It generates a transient scratchpad analysis id to separate the analysis logic in redux
 * between the analysis loaded into reports and the analysis loaded into scratchpads.
 */
const useScratchpadAnalysis = ({
  analysisId,
  refresh = false,
  shouldLoad = false,
}: UseScratchpadAnalysisProps) => {
  const dispatch = useAppDispatch();
  const scratchpadAnalysisId = generateScratchpadAnalysisId(analysisId);

  const isAnalysisLoadedMemoSelector = useMemo(isAnalysisLoadedSelector, []);
  const isScratchpadAnalysisLoaded = useAppSelector((state) =>
    isAnalysisLoadedMemoSelector(state, scratchpadAnalysisId),
  );

  const loadAnalysisInStore = useCallback(
    ({ analysis }: IAnalysisQuery) => {
      assertNotNil(analysis);
      batch(() => {
        dispatch(
          populateAnalysisThunk({
            analysis: {
              ...analysis,
              id: scratchpadAnalysisId,
            },
          }),
        );
        dispatch(loadAnalysisAction(scratchpadAnalysisId));
      });
    },
    [dispatch, scratchpadAnalysisId],
  );

  const { isDebugInUrl } = useCurrentUrl();
  const { refetch } = useQuery<IAnalysisQuery, IAnalysisQueryVariables>(
    GET_ANALYSIS,
    {
      variables: {
        analysisId,
        refresh,
      },
      onCompleted: loadAnalysisInStore,
      // this hook is called in report page, therefore, this query will be requested in debug page
      // which should be skipped.
      skip: isDebugInUrl || (!shouldLoad && !isScratchpadAnalysisLoaded),
    },
  );

  const refreshScratchpadAnalysis = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    scratchpadAnalysisId,
    isScratchpadAnalysisLoaded,
    refreshScratchpadAnalysis,
  };
};

export default useScratchpadAnalysis;
